import { createGlobalStyle, css } from 'styled-components';
import font from '@/fonts/sourceSans';
import { MOONBaseTheme, MOONExtrasTheme, MOONSpacingTheme, MOONTypographyTheme } from './IThemeConfig';

const moonSpacings: MOONSpacingTheme = {
    containerWidth: '1368px',

    '4xs': '2px',
    '3xs': '6px',
    '2xs': '12px',
    xs: '18px',
    sm: '24px',
    md: '30px',
    lg: '36px',
    xl: '42px',
    '2xl': '48px',
    '3xl': '54px',
    '4xl': '60px'
};

const moonExtras: MOONExtrasTheme = {
    opacity: {
        disabled: '48%'
    },
    animation: {
        arrival: 'ease-out 0.3s',
        departure: 'ease-in 0.2s'
    },
    radius: {
        xs: '2px',
        sm: '6px',
        md: '12px',
        xl: '18px'
    }
};

const moonTypography: MOONTypographyTheme = {
    paragraph: {
        xs: {
            size: '10px',
            lineHeight: '16px'
        },
        sm: {
            size: '12px',
            lineHeight: '18px'
        },
        md: {
            size: '14px',
            lineHeight: '24px'
        },
        lg: {
            size: '16px',
            lineHeight: '28px'
        },
        xl: {
            size: '18px',
            lineHeight: '28px'
        }
    },
    uiType: {
        '3xs': {
            R: {
                size: '9px',
                lineHeight: '16px'
            },
            SU: {
                size: '9px',
                lineHeight: '16px',
                letterSpacing: '1px'
            },
            RU: {
                size: '9px',
                lineHeight: '16px',
                letterSpacing: '1px'
            }
        },
        '2xs': {
            R: {
                size: '10px',
                lineHeight: '16px'
            },
            RL: {
                size: '10px',
                lineHeight: '16px'
            },
            RU: {
                size: '10px',
                lineHeight: '16px'
            },
            S: {
                size: '10px',
                lineHeight: '16px'
            },
            SL: {
                size: '10px',
                lineHeight: '16px'
            },
            SU: {
                size: '10px',
                lineHeight: '16px'
            }
        },
        xs: {
            R: {
                size: '12px',
                lineHeight: '16px'
            },
            RL: {
                size: '12px',
                lineHeight: '16px'
            },
            S: {
                size: '12px',
                lineHeight: '16px'
            },
            SU: {
                size: '12px',
                lineHeight: '16px'
            },
            SL: {
                size: '12px',
                lineHeight: '16px'
            }
        },
        sm: {
            R: {
                size: '14px',
                lineHeight: '21px'
            },
            S: {
                size: '14px',
                lineHeight: '21px'
            },
            RL: {
                size: '14px',
                lineHeight: '21px'
            },
            SL: {
                size: '14px',
                lineHeight: '21px'
            }
        },
        md: {
            R: {
                size: '16px',
                lineHeight: '24px'
            },
            S: {
                size: '16px',
                lineHeight: '24px'
            },
            RL: {
                size: '16px',
                lineHeight: '24px'
            },
            SL: {
                size: '16px',
                lineHeight: '24px'
            }
        },
        lg: {
            R: {
                size: '18px',
                lineHeight: '24px'
            },
            S: {
                size: '18px',
                lineHeight: '24px'
            }
        },
        xl: {
            R: {
                size: '20px',
                lineHeight: '32px'
            },
            S: {
                size: '20px',
                lineHeight: '32px'
            }
        },
        '2xl': {
            R: {
                size: '24px',
                lineHeight: '32px'
            },
            S: {
                size: '24px',
                lineHeight: '32px'
            }
        },
        '3xl': {
            R: {
                size: '32px',
                lineHeight: '40px'
            },
            S: {
                size: '32px',
                lineHeight: '40px'
            }
        }
    },
    weight: {
        regular: 400,
        semiBold: 600,
        bold: 700
    },
    icon: {
        xs: '6px',
        sm: '12px',
        md: '18px',
        lg: '24px'
    }
};

export const baseTheme: MOONBaseTheme = {
    typography: moonTypography,
    spacing: moonSpacings,
    extras: moonExtras
};

export const isDarkMode = () => {
    'use client';

    if (typeof window === 'undefined') {
        return;
    }

    return localStorage.getItem('theme') === 'dark';
};

export const toggleTheme = () => {
    'use client';

    if (typeof window === 'undefined') {
        return;
    }

    localStorage.setItem('theme', isDarkMode() ? 'light' : 'dark');
};

export const GlobalStyles = createGlobalStyle`
    html, body {
        min-height: 100vh;
        height: 100%;
    }

    body {
        background-color: ${({ theme }) => theme.colors.background};
        scroll-behavior: smooth;
        margin: 0;
        color: ${({ theme }) => theme.colors.palette.grey_700};
    }

    p {
        margin-block: unset;
        font-family: 'Source Sans 3', sans-serif;
    }
    
    input, textarea {
        font-family: 'Source Sans 3', sans-serif;
    }

    p, span {
        cursor: default;
    }

    * {
        box-sizing: border-box;
    }
    
    ul {
        margin: 0;
        margin-block: 0;
        margin-inline: 0;
        padding-inline: 25px 0;
    }
    
    div[role="dialog"] {
        outline: none;
    }
    
    // https://aykevl.nl/2014/09/fix-jumping-scrollbar
    // The media query must be uncommented and tuned once RWD is added
    // TODO Check this solution because "invisible" margin from the post is very visible and makes any page scrollable by x
    @media screen /*and (min-width: 960px)*/ {
        body {
            margin-left: calc(100vw - 100%);
            margin-right: 0;
            padding-right: 0 !important;
        }
    }

    /* Guided tour popover styles */
    .driver-popover {
        padding: ${({ theme }) => theme.spacing.sm} 0 0 0;
        min-width: 364px;
        border-radius: ${({ theme }) => theme.extras.radius.md};

        span, strong, div, header, button {
            font-family: ${font.style.fontFamily};
        }
    
        .driver-popover-title {
            color: ${({ theme }) => theme.colors.palette.grey_800};
            font-size: ${({ theme }) => theme.typography.uiType['xl'].S.size};
            font-weight: ${({ theme }) => theme.typography.weight.semiBold};
            line-height: ${({ theme }) => theme.typography.uiType['xl'].S.lineHeight};
            padding: 0 ${({ theme }) => theme.spacing.sm} 0;
        }

        .driver-popover-description {
            color: ${({ theme }) => theme.colors.palette.grey_800};
            font-size: ${({ theme }) => theme.typography.uiType['sm'].S.size};
            font-weight: ${({ theme }) => theme.typography.weight.regular};
            line-height: ${({ theme }) => theme.typography.uiType['sm'].S.lineHeight};

            padding: 0 ${({ theme }) => theme.spacing.sm} 0;

            strong {
                font-weight: ${({ theme }) => theme.typography.weight.semiBold};
            }
        }

        .driver-popover-footer {
            padding: 12px;
            margin: 0;
        }

        .driver-popover-navigation-btns {
            padding: 0;
            display: flex;
            justify-content: space-between;
        }

        .guidedTourProgress {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: ${({ theme }) => theme.spacing.lg};
            padding: 0 ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing['2xs']};

            .progressBarWrapper {
                width: 100%;
                padding: 2px;
                border: 1px solid ${({ theme }) => theme.colors.palette.grey_200};
                border-radius: ${({ theme }) => theme.extras.radius.sm};
                box-sizing: content-box;

                .progressBar {
                    display: block;
                    height: 6px;
                    background-color: ${({ theme }) => theme.colors.palette.blue_500};
                    border-radius: ${({ theme }) => theme.extras.radius.sm};
                }
            }

            .guidedTourProgressText {
                font-size: ${({ theme }) => theme.typography.uiType['sm'].S.size};
                font-weight: ${({ theme }) => theme.typography.weight.regular};
                line-height: ${({ theme }) => theme.typography.uiType['sm'].S.lineHeight};
                color: ${({ theme }) => theme.colors.palette.grey_800};
                white-space: nowrap;
            }

            .guidedTourCurrentStep {
                color: ${({ theme }) => theme.colors.palette.blue_500};
            }
        }

        &&& .driver-popover-navigation-btns {
            display: flex;
            justify-content: space-between;
        }

        &&&.singleButton .driver-popover-navigation-btns{
            justify-content: flex-end;
        }

        &&& .driver-popover-next-btn, &&& .driver-popover-prev-btn {
            display: inline-flex;
            padding: 7.5px 12px;
            border: 0;
            height: 36px;
            border-radius: ${({ theme }) => theme.extras.radius.sm};
            align-items: center;
            text-shadow: none;
            font-weight: ${({ theme }) => theme.typography.weight.semiBold};
            font-size: ${({ theme }) => theme.typography.uiType['sm'].S.size};
            line-height: ${({ theme }) => theme.typography.uiType['sm'].S.lineHeight};
            font-style: normal;
        }

        &&& .driver-popover-next-btn {
            transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
            color: ${({ theme }) => theme.colors.palette.white};
            background-color: ${({ theme }) => theme.colors.palette.blue_500};

            &:hover {
                background-color: ${({ theme }) => theme.colors.palette.blue_600};
            }

            &:active {
                background-color: ${({ theme }) => theme.colors.palette.blue_700};
            }
        }

        &&& .driver-popover-prev-btn {
            transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
            color: ${({ theme }) => theme.colors.palette.blue_500};
            background-color: transparent;

            &:hover {
                background-color: ${({ theme }) => theme.colors.palette.blue_100};
                color: ${({ theme }) => theme.colors.palette.blue_600};
            }

            &:active {
                background-color: ${({ theme }) => theme.colors.palette.blue_200};
                color: ${({ theme }) => theme.colors.palette.blue_700};
            }
        }
    }

    &&&:not(body):has( > .driver-active-element) {
        overflow: unset !important;
    }
}
`;

// Colors set directly here, because for some reason there's an additional comma rendered when theme used, breaking the styles
export const ScrollbarStyles = css`
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-corner {
        display: none;
    }

    &::-webkit-scrollbar-button {
        display: none;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #D1D1D6;
        border-radius: 24px;

        &:hover {
            background-color: #959DA6;
        }
    }

    &::-webkit-scrollbar-track {
        border-radius: 36px;
        background-color: transparent;
    }
`;

export const ScrollbarOverlayStyles = css`
    @supports (scrollbar-width: thin) {
        scrollbar-width: thin;
        scrollbar-color: ${({ theme }) => theme.colors.palette.grey_500} transparent;
    }
`;
